import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import { globalModel } from "../../global/GlobalModel";
import { LobbyModel } from "../lobby/LobbyModel";
import { RelatedSpacesFollowsPanel } from "../lobby/RelatedSpacesFollowsPanel";
import type { ListTab } from "../lobby/relatedSpacesFollowsLogic";
import { syncOrdersFallbackFromRelatedSpaceCards } from "../lobby/syncOrdersFallbackFromCards";

export type RelatedSpacesOrdersModalsApi = {
  openRelatedSpaces: (opts?: { initialTab?: ListTab }) => void;
  /** @deprecated 请直接 navigate('/orders')；保留兼容旧调用 */
  openOrdersSpace: (opts?: { spaceId?: string | null }) => void;
  relatedModalOpen: boolean;
  relatedModalTab: ListTab;
};

const Ctx = createContext<RelatedSpacesOrdersModalsApi | null>(null);

export function useRelatedSpacesOrdersModals(): RelatedSpacesOrdersModalsApi {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useRelatedSpacesOrdersModals must be used inside provider");
  }
  return v;
}

const MODAL_BODY_PAD = 24;
const MODAL_HEADER_PAD_X = 24;

const shellModalStyles = {
  content: {
    height: "80vh",
    maxHeight: "80vh",
    borderRadius: 0,
    padding: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
  },
  header: {
    flexShrink: 0,
    margin: 0,
    padding: `20px ${MODAL_HEADER_PAD_X}px 16px`,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: MODAL_BODY_PAD,
    boxSizing: "border-box" as const,
  },
};

/** 全站「与我相关的空间」弹框 */
export function RelatedSpacesOrdersModalsProvider(props: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [relatedTab, setRelatedTab] = useState<ListTab>("follow");

  const followsLobbyModel = useMemo(
    () => new LobbyModel("follows", "00000000"),
    []
  );

  useEffect(() => {
    if (!relatedOpen) return;
    void followsLobbyModel.load();
  }, [relatedOpen, followsLobbyModel]);

  useEffect(() => {
    if (!relatedOpen || !followsLobbyModel.loaded) return;
    syncOrdersFallbackFromRelatedSpaceCards(followsLobbyModel.cards);
  }, [relatedOpen, followsLobbyModel.loaded, followsLobbyModel.cards]);

  const openRelatedSpaces = useCallback((opts?: { initialTab?: ListTab }) => {
    const t = opts?.initialTab ?? "follow";
    setRelatedTab(t);
    setRelatedOpen(true);
  }, []);

  const openOrdersSpace = useCallback((_opts?: { spaceId?: string | null }) => {
    navigate("/orders");
  }, [navigate]);

  const api = useMemo<RelatedSpacesOrdersModalsApi>(
    () => ({
      openRelatedSpaces,
      openOrdersSpace,
      relatedModalOpen: relatedOpen,
      relatedModalTab: relatedTab,
    }),
    [openRelatedSpaces, openOrdersSpace, relatedOpen, relatedTab]
  );

  return (
    <Ctx.Provider value={api}>
      {props.children}
      <Modal
        title="与我相关的空间"
        open={relatedOpen}
        onCancel={() => setRelatedOpen(false)}
        footer={null}
        width={980}
        centered
        destroyOnClose
        zIndex={1100}
        styles={shellModalStyles}
      >
        <RelatedSpacesFollowsPanel
          model={followsLobbyModel}
          listTab={relatedTab}
          onTabChange={setRelatedTab}
          onCardNavigate={(id) => {
            setRelatedOpen(false);
            navigate(`/space/${id}/home`);
          }}
          onEnterSpaceIdHome={() => {
            setRelatedOpen(false);
            navigate("/");
          }}
          showPageHeading={false}
        />
      </Modal>
    </Ctx.Provider>
  );
}
